import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { ONGOING_PROJECTS } from "../data/content";

const Ongoing = () => {
  const lenis = useLenis();
  const enquire = (e) => {
    e.preventDefault();
    lenis?.scrollTo("#real-estate", { offset: -64, duration: 1.4 });
  };

  return (
    <section id="ongoing" data-testid="ongoing-section" className="relative bg-white border-b border-slate-200">
      <span className="absolute top-3 left-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <span className="absolute bottom-3 right-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-blueprint-accent mb-6">
              Live Sites
            </p>
            <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl md:text-5xl text-prussian">
              Now on the rise.
            </h2>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
            {String(ONGOING_PROJECTS.length).padStart(2, "0")} Active{" "}
            {ONGOING_PROJECTS.length === 1 ? "Site" : "Sites"} — Hyderabad
          </p>
        </motion.div>

        <div className={`grid gap-5 ${ONGOING_PROJECTS.length === 1 ? "max-w-3xl" : "md:grid-cols-3"}`}>
          {ONGOING_PROJECTS.map((p, i) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`ongoing-card-${i + 1}`}
              className="border border-blueprint-line bg-white flex flex-col hover:shadow-[6px_6px_0px_#0A192F] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
            >
              <div className="flex items-center justify-between border-b border-blueprint-line px-6 py-3">
                <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-slate-400">
                  Site / 0{i + 1}
                </span>
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white bg-blueprint-accent px-2 py-1">
                  Under Construction
                </span>
              </div>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <h3 className="font-display font-bold tracking-tight text-2xl text-prussian">
                  {p.name}
                </h3>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 mt-2">
                  {p.type} — {p.units}
                </p>
                <p className="mt-4 flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                  <MapPin size={15} className="text-blueprint-accent shrink-0 mt-0.5" />
                  {p.address}
                </p>
                {p.specs && (
                  <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 border-t border-l border-slate-100">
                    {p.specs.map((s) => (
                      <div key={s.label} className="border-b border-r border-slate-100 px-4 py-3">
                        <dt className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-400">
                          {s.label}
                        </dt>
                        <dd className="mt-1 text-sm text-prussian">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <div className="mt-6">
                  <div className="flex justify-between font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mb-2">
                    <span>Construction Progress</span>
                    <span className="text-prussian">{p.progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 border border-slate-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${p.progress}%` }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 1.2, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      data-testid={`ongoing-progress-${i + 1}`}
                      className="h-full bg-blueprint-accent"
                    />
                  </div>
                </div>
                <div className="mt-auto pt-5 mt-6 border-t border-slate-100 flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500">
                    Handover: <span className="text-prussian">{p.handover}</span>
                  </span>
                  <a
                    href="#real-estate"
                    onClick={enquire}
                    data-testid={`ongoing-enquire-button-${i + 1}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-blueprint-accent hover:text-prussian transition-colors duration-300"
                  >
                    Enquire <ArrowUpRight size={13} />
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Ongoing;
